import { useEffect, useRef, useState } from "react";
import templateUrl from "../../assets/petloc-label-template.png";

interface Props {
  catId: string;
  name?: string | null;
  showName: boolean;
  /** Stück-Code (publicId), wird dezent unten rechts gezeigt. */
  code?: string | null;
  /** Vorschau-Breite in px (Höhe folgt dem Template-Seitenverhältnis). */
  width?: number;
}

// Template-Maße (Pixel) und Layout — identisch zu print_petlabel.py.
const TW = 200;
const TH = 96;
const MARGIN = 3;
const TEXT_GAP = 7;
const NAME_FONT = 22;
const CTA_FONT = 14;
const MIN_FONT = 7;
const QR_PX = Math.min(TH - 2 * MARGIN, Math.floor(TW / 2) - 2 * MARGIN); // 90
const SCAN_CTA = "Scann mich!";
const CTA_GAP = 2; // kleiner Abstand vertikaler CTA -> QR (linksbündig am QR)
const CTA_SCALE = 0.9; // CTA-Schrift minimal kleiner

/** Bounding-Box des Brandings (dunkle Pixel) im Template; null falls keins. */
function detectBrand(img: HTMLImageElement): { left: number; top: number } | null {
  const off = document.createElement("canvas");
  off.width = TW;
  off.height = TH;
  const octx = off.getContext("2d");
  if (!octx) return null;
  octx.drawImage(img, 0, 0, TW, TH);
  const { data } = octx.getImageData(0, 0, TW, TH);
  let left = TW;
  let top = TH;
  let found = false;
  for (let y = 0; y < TH; y++) {
    for (let x = 0; x < TW; x++) {
      const i = (y * TW + x) * 4;
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      const alpha = data[i + 3] ?? 0;
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      if (alpha > 32 && lum < 128) {
        found = true;
        if (x < left) left = x;
        if (y < top) top = y;
      }
    }
  }
  return found ? { left, top } : null;
}

export function LabelPreview({ catId, name, showName, code, width = 360 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrImg, setQrImg] = useState<HTMLImageElement | null>(null);
  const [tplImg, setTplImg] = useState<HTMLImageElement | null>(null);

  // Der QR zeigt auf den Stück-Code (falls vorhanden), sonst auf das Tier.
  const url = `${window.location.origin}/c/${code ?? catId}`;
  const scale = width / TW;
  const height = Math.round(TH * scale);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setTplImg(img);
    img.src = templateUrl;
  }, []);

  useEffect(() => {
    let active = true;
    import("qrcode").then(async (QRCode) => {
      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: Math.round(QR_PX * scale * 2), // 2× für Schärfe
        color: { dark: "#000000", light: "#FFFFFF" },
      });
      const img = new Image();
      img.onload = () => active && setQrImg(img);
      img.src = dataUrl;
    });
    return () => {
      active = false;
    };
  }, [url, scale]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !tplImg || !qrImg) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = width;
    canvas.height = height;
    const s = (v: number) => v * scale;

    // Hintergrund (Template mit Branding)
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(tplImg, 0, 0, width, height);

    // QR links (scharfe Module)
    const qrY = Math.round((TH - QR_PX) / 2);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrImg, s(MARGIN), s(qrY), s(QR_PX), s(QR_PX));
    ctx.imageSmoothingEnabled = true;

    const truncate = (text: string, max: number) => {
      if (ctx.measureText(text).width <= max) return text;
      let t = text;
      while (t && ctx.measureText(t + "…").width > max) t = t.slice(0, -1);
      return t ? t + "…" : "";
    };

    const brand = detectBrand(tplImg);

    // --- vertikales "Scann mich" im Streifen rechts vom QR (liest unten->oben) ---
    const bandLeft = s(MARGIN + QR_PX + TEXT_GAP);
    const bandRight = brand ? s(brand.left - TEXT_GAP) : bandLeft + s(18);
    const bandW = bandRight - bandLeft;
    let ctaRight = s(MARGIN + QR_PX); // rechte Kante des CTA (falls keiner: QR-Kante)
    if (bandW > 4) {
      const maxLen = s(TH - 2 * MARGIN); // verfügbare Höhe = Textlänge nach Drehung
      let px = s(CTA_FONT + 4);
      const minPx = s(MIN_FONT);
      ctx.font = `600 ${px}px Inter, sans-serif`;
      while (px > minPx && (ctx.measureText(SCAN_CTA).width > maxLen || px > bandW * 0.92)) {
        px -= 1;
        ctx.font = `600 ${px}px Inter, sans-serif`;
      }
      px = Math.max(minPx, px * CTA_SCALE); // minimal kleiner
      ctx.font = `600 ${px}px Inter, sans-serif`;
      const cta = truncate(SCAN_CTA, maxLen);
      // direkt rechts am QR (linksbündig): linke Kante + halbe Texthöhe
      const ctaX = s(MARGIN + QR_PX + CTA_GAP) + px / 2;
      ctx.save();
      ctx.fillStyle = "#111";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.translate(ctaX, height / 2);
      ctx.rotate(Math.PI / 2); // oben -> unten (180° gedreht)
      ctx.fillText(cta, 0, 0);
      ctx.restore();
      ctaRight = ctaX + px / 2;
    }

    // --- Name horizontal, linksbündig direkt rechts vom CTA ---
    if (showName && name) {
      const regionLeft = ctaRight + s(TEXT_GAP);
      const regionRight = s(TW - MARGIN);
      const regionTop = s(MARGIN);
      const regionBottom = brand ? s(brand.top - TEXT_GAP) : s(TH - MARGIN);
      const maxW = regionRight - regionLeft;
      const maxH = regionBottom - regionTop;
      if (maxW > 4 && maxH > 4) {
        let px = s(NAME_FONT);
        const minPx = s(MIN_FONT);
        ctx.font = `700 ${px}px Inter, sans-serif`;
        while (px > minPx && ctx.measureText(name).width > maxW) {
          px -= 1;
          ctx.font = `700 ${px}px Inter, sans-serif`;
        }
        if (px > maxH) {
          px = Math.max(minPx, maxH);
          ctx.font = `700 ${px}px Inter, sans-serif`;
        }
        ctx.fillStyle = "#111";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(truncate(name, maxW), regionLeft, (regionTop + regionBottom) / 2);
      }
    }

    // Stück-Code dezent, aber lesbar unten rechts.
    if (code) {
      ctx.fillStyle = "#475569";
      ctx.font = `${s(8)}px ui-monospace, Menlo, monospace`;
      ctx.textAlign = "right";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(code, width - s(MARGIN), height - s(MARGIN));
    }
  }, [tplImg, qrImg, name, showName, code, width, height, scale]);

  return (
    <canvas
      ref={canvasRef}
      className="rounded-lg border border-slate-200 w-full h-auto"
      style={{ aspectRatio: `${TW} / ${TH}` }}
    />
  );
}
